import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';

import { apiUrls } from '../../../constants';
import { remove1FormEndOfSymbolName } from '../../../tools';
import { SieveModel } from '../../../modules/shared/models';
import { ContractInfo } from '../models/contract-info.model';
import { BaseService } from '../../../modules/shared/services';
import {
	CashSettlement,
	OptionSide,
	PhysicalSettlement,
	PositionResponse,
	SettlementRequest
} from '../models/option.model';
import { PositionHistoryResponse, OptionCustomerStatus, Position } from '../models/option.model';
import { BaseOrderResponse, From, IBaseOMSOrder, IDeleteResponseModel } from '../../../modules/order';

@Injectable({ providedIn: 'root' })
export class OptionService extends BaseService {
	private httpClient = inject(HttpClient);

	get optionOrderUrl(): string {
		return this.mtsPath + apiUrls.option;
	}
	getContractForSymbol(symbolIsin): Observable<ContractInfo> {
		return this.httpService.get<any>(`${this.mtsPath + apiUrls.option}Contracts/${symbolIsin}/symbol`);
	}
	get() {
		return this.httpService.get<PositionResponse[]>(`${this.optionOrderUrl}Positions`).pipe(
			map(res =>
				res.map(res => {
					return { ...res, symbolName: remove1FormEndOfSymbolName(res.symbolName) } as Position;
				})
			)
			// catchError(error => of({ isSuccessful: false, message: error?.error?.detail } as BaseOrderResponse))
		);
	}
	getHistory(filter: Partial<SieveModel>) {
		return this.httpService.post<PositionHistoryResponse>(`${this.optionOrderUrl}Positions/history`, {
			body: { ...filter }
		});
	}
	getCashSettlements(positionId: string): Observable<CashSettlement[]> {
		return this.httpService.get(`${this.optionOrderUrl}Positions/${positionId}/settlements/cash`);
	}
	getPhysicalSettlements(positionId: string): Observable<PhysicalSettlement[]> {
		return this.httpService.get(`${this.optionOrderUrl}Positions/${positionId}/settlements/physical`);
	}
	getCashSettlement(id: string) {
		return this.httpService.get<CashSettlement>(`${this.optionOrderUrl}Positions/settlements/${id}/cash`);
	}
	getPhysicalSettlement(id: string) {
		return this.httpService.get<PhysicalSettlement>(`${this.optionOrderUrl}Positions/settlements/${id}/physical`);
	}
	getCustomerStatus() {
		return this.httpService
			.get<OptionCustomerStatus>(`${this.optionOrderUrl}Customers/status`)
			.pipe(catchError(() => of({ margin: 0, status: -1 } as OptionCustomerStatus)));
	}
	createOrder(value: IBaseOMSOrder) {
		const { side, ...order } = value;
		return this.httpClient
			.post<BaseOrderResponse>(
				`${this.optionOrderUrl}Orders/${OptionSide[side]}`,
				{ ...order },
				{ observe: 'response' }
			)
			.pipe(
				map((response: any) => {
					const id = response?.headers.get('X-Order-Id');
					return { isSuccessful: true, id } as BaseOrderResponse;
				}),
				catchError(error => of({ isSuccessful: false, message: error?.error?.detail } as BaseOrderResponse))
			);
	}
	updateOrder(value: IBaseOMSOrder) {
		const { side, ...order } = value;
		return this.httpClient
			.put<BaseOrderResponse>(
				`${this.optionOrderUrl}Orders/${OptionSide[side]}`,
				{ ...order },
				{ observe: 'response' }
			)
			.pipe(
				map((response: any) => {
					const id = response?.headers.get('X-Order-Id');
					return { isSuccessful: true, id } as BaseOrderResponse;
				}),
				catchError(error => of({ isSuccessful: false, message: error?.error?.detail } as BaseOrderResponse))
			);
	}
	deleteOrder(orderId: string, orderFrom: From.OrbisOptionMobile | From.OrbisOptionDesktop) {
		return this.httpService
			.delete<IDeleteResponseModel>(`${this.optionOrderUrl + 'Orders'}`, { body: { orderId, orderFrom } })
			.pipe(catchError(error => of({ isSuccessful: false, message: error?.error?.detail } as BaseOrderResponse)));
	}
	createCashSettlement(value: SettlementRequest) {
		return this.httpClient
			.post(`${this.optionOrderUrl}Positions/settlements/cash`, { ...value }, { observe: 'response' })
			.pipe(
				map((response: any) => {
					const id = response?.headers.get('X-Settlement-Id');
					return {
						isSuccessful: true,
						id,
						message: 'درخواست تسویه نقدی با موفقیت ثبت شد'
					} as BaseOrderResponse;
				}),
				catchError(error =>
					of({
						isSuccessful: false,
						message: error?.error ?? error?.error?.detail ?? 'خطا در ثبت تسویه'
					} as BaseOrderResponse)
				)
			);
	}
	createPhysicalSettlement(value: SettlementRequest) {
		return this.httpClient
			.post(`${this.optionOrderUrl}Positions/settlements/physical`, { ...value }, { observe: 'response' })
			.pipe(
				map((response: any) => {
					const id = response?.headers.get('X-Settlement-Id');
					return {
						isSuccessful: true,
						id,
						message: 'درخواست تسویه فیزیکی با موفقیت ثبت شد'
					} as BaseOrderResponse;
				}),
				catchError(error =>
					of({
						isSuccessful: false,
						message: error?.error ?? error?.error?.detail ?? 'خطا در ثبت تسویه'
					} as BaseOrderResponse)
				)
			);
	}
	deleteSettlementRequest(settlementId: string) {
		return this.httpService.delete(`${this.optionOrderUrl}positions/settlements/${settlementId}`).pipe(
			map(() => ({ isSuccessful: true, message: 'حذف  تسویه  با موفقیت انجام شد' }) as BaseOrderResponse),
			catchError(error =>
				of({
					isSuccessful: false,
					message: error?.error ?? error?.error?.detail ?? 'خطا در حذف درخواست'
				} as BaseOrderResponse)
			)
		);
	}
}
